/**
 * Pompeii — Facets JS
 * Handles filter form submissions via AJAX for seamless page updates.
 */

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = this.debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.bindAll();
  }

  bindAll() {
    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit);
      facetForm.addEventListener('change', this.debouncedOnSubmit);
    }

    // Active filter tag removal
    const activeTags = this.querySelectorAll('.facets__active-tag');
    activeTags.forEach((tag) => {
      tag.addEventListener('click', this.onActiveFilterClick);
    });

    // Panel toggle
    this.bindPanelToggle();

    // Price range sliders
    this.bindPriceSliders();
  }

  bindPanelToggle() {
    const toggle = this.querySelector('[data-facets-toggle]');
    const panel = this.querySelector('#FacetsPanel');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', !expanded);
      panel.setAttribute('aria-hidden', expanded);
      panel.classList.toggle('facets__panel--open');
      if (!expanded) {
        toggle.textContent = toggle.textContent.replace('+', '\u2212');
      } else {
        toggle.textContent = toggle.textContent.replace('\u2212', '+');
      }
    });

    // Auto-open if active filters exist
    if (panel.hasAttribute('data-has-active')) {
      toggle.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      panel.classList.add('facets__panel--open');
      toggle.textContent = toggle.textContent.replace('+', '\u2212');
    }
  }

  bindPriceSliders() {
    this.querySelectorAll('.facets__price-range').forEach((container) => {
      const rangeMax = parseFloat(container.dataset.rangeMax) || 100;
      const slider = container.querySelector('.facets__slider');
      const fill = container.querySelector('.facets__slider-fill');
      const handleMin = container.querySelector('.facets__slider-handle--min');
      const handleMax = container.querySelector('.facets__slider-handle--max');
      const hiddenMin = container.querySelector('.facets__price-hidden-min');
      const hiddenMax = container.querySelector('.facets__price-hidden-max');
      const labelMin = container.querySelector('.facets__price-label-min');
      const labelMax = container.querySelector('.facets__price-label-max');
      if (!slider || !handleMin || !handleMax) return;

      let minVal = parseFloat(container.dataset.currentMin) || 0;
      let maxVal = parseFloat(container.dataset.currentMax) || rangeMax;

      const update = () => {
        const minPct = (minVal / rangeMax) * 100;
        const maxPct = (maxVal / rangeMax) * 100;
        handleMin.style.left = minPct + '%';
        handleMax.style.left = maxPct + '%';
        fill.style.left = minPct + '%';
        fill.style.right = (100 - maxPct) + '%';
        handleMin.setAttribute('aria-valuenow', minVal.toFixed(2));
        handleMax.setAttribute('aria-valuenow', maxVal.toFixed(2));
        if (labelMin) labelMin.textContent = minVal.toFixed(2).replace('.', ',') + ' EUR';
        if (labelMax) labelMax.textContent = maxVal.toFixed(2).replace('.', ',') + ' EUR';
      };

      update();

      const getValFromX = (clientX) => {
        const rect = slider.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        return Math.round(pct * rangeMax * 100) / 100;
      };

      const startDrag = (handle, isMin) => {
        const onMove = (e) => {
          const x = e.touches ? e.touches[0].clientX : e.clientX;
          let val = getValFromX(x);
          if (isMin) {
            val = Math.min(val, maxVal - 1);
            minVal = Math.max(0, val);
          } else {
            val = Math.max(val, minVal + 1);
            maxVal = Math.min(rangeMax, val);
          }
          update();
        };

        const onEnd = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onEnd);
          document.removeEventListener('touchmove', onMove);
          document.removeEventListener('touchend', onEnd);
          // Update hidden inputs and trigger form change
          if (hiddenMin) hiddenMin.value = minVal > 0 ? minVal.toFixed(2) : '';
          if (hiddenMax) hiddenMax.value = maxVal < rangeMax ? maxVal.toFixed(2) : '';
          const form = container.closest('form');
          if (form) form.dispatchEvent(new Event('change', { bubbles: true }));
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchmove', onMove, { passive: true });
        document.addEventListener('touchend', onEnd);
      };

      handleMin.addEventListener('mousedown', (e) => { e.preventDefault(); startDrag(handleMin, true); });
      handleMin.addEventListener('touchstart', (e) => { startDrag(handleMin, true); }, { passive: true });
      handleMax.addEventListener('mousedown', (e) => { e.preventDefault(); startDrag(handleMax, false); });
      handleMax.addEventListener('touchstart', (e) => { startDrag(handleMax, false); }, { passive: true });
    });
  }

  onSubmitHandler(event) {
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    this.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString());
  }

  renderPage(searchParams) {
    const url = `${window.location.pathname}?${searchParams}`;
    const productGrid = document.getElementById('product-grid');
    const productGridContainer = document.getElementById('ProductGridContainer');

    if (productGridContainer) {
      productGridContainer.classList.add('loading');
    }

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Update product grid
        const newGrid = html.getElementById('product-grid');
        if (newGrid && productGrid) {
          productGrid.innerHTML = newGrid.innerHTML;
        }

        // Replace filters with server response (same-origin, safe)
        const newFilters = html.querySelector('facet-filters-form');
        if (newFilters) {
          this.replaceChildren(...newFilters.cloneNode(true).childNodes);
          this.bindAll();
        }

        // Update URL
        history.pushState({}, '', url);

        // Re-setup collection handlers (for hover, quick-add)
        if (typeof window.setupCollectionHandlers === 'function') {
          window.setupCollectionHandlers();
        }
      })
      .catch(console.error)
      .finally(() => {
        if (productGridContainer) {
          productGridContainer.classList.remove('loading');
        }
      });
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);
